import { createSelector, createStructuredSelector } from 'reselect';
import { idSelector } from 'selectors/app';
import _ from 'lodash';

const tariffIdPropSelector = (state, { tariffId }) => tariffId;

// helping functions
const findMinPrice = (purchaseInfo, key) => {
  let result = null;
  if (purchaseInfo && key) {
    const tariffs = _.get(purchaseInfo, `${key}.subscriptions.0.tariffs`);
    if (_.isArray(tariffs)) {
      for (let i = 0, l = tariffs.length; i < l; ++i) {
        if (result === null || tariffs[i].price < result) {
          result = tariffs[i].price;
        }
      }
    }
  }
  return result;
};

const getTariffInfo = (purchaseInfo, type) => {
  const result = [];
  const subscription = _.get(purchaseInfo, `${type}.subscriptions.0`);
  const tariffs = _.get(subscription, 'tariffs');
  let qualityGroup;
  if (_.isArray(tariffs)) {
    for (let i = 0, l = tariffs.length; i < l; ++i) {
      qualityGroup = _.get(tariffs[i], 'quality.group', '');
      if (!tariffs[i].payment_systems) {
        tariffs[i].payment_systems = [];
      }
      result.push({
        id: tariffs[i].tariff_id,
        price: tariffs[i].price,
        period: tariffs[i].period,
        priceText: `${tariffs[i].price} ${subscription.currency}`,
        currency: subscription.currency,
        isBought: tariffs[i].is_bought,
        qualityGroup,
        qualityGroup_lower: qualityGroup.toLowerCase(),
        type,
        paymentSystems: tariffs[i].payment_systems.map((system) => system.payment_system_id),
      });
    }
  }
  return result;
};

export const configuration = (state) => state.configuration;
export const seasonItems = (state) => state.season;

export const movieInfo = (state, props) => {
  const id = props.id || _.get(state, 'app.id');
  let movie = state.movie[id] || {};
  const episode = state.episode[id];
  const season = episode ? _.find(state.season, { episode_list: [episode.id] }) : state.season[id];

  movie.single_episode = state.season
    ? Object.keys(state.season).length === 1 &&
      _.get(state.season[Object.keys(state.season)[0]], 'episode_list', []).length <= 1
    : false;

  if (season) {
    const movieBySeason = _.find(state.movie, { season_list: [season.id] });
    if (movieBySeason) {
      movie = { ...movieBySeason };
      movie.title += ` - ${season.title}`;
      movie.season_id = season.id;
      if (episode) {
        movie.episode_id = episode.id;
        movie.title += ` - ${episode.title}`;
      }
    }
  } else if (!movie.id) {
    movie = _.find(state.movie, { trailer_id: id }) || {};
  }
  return movie;
};

export const isVideoInfoLoading = (state) => {
  const id = _.get(state, 'app.id');
  let movie = state.movie[id];
  if (!movie) {
    movie = { isLoading: false };
  }
  return movie.isLoading;
};

/**
 * @todo Clarify usage and rename according to functions, yetAnotherMovieInfoButFull, for example.
 * @param state
 * @param props
 * @return {{}}
 */
export const movieErrorInfo = (state, props) => {
  const id = props.id || _.get(state, 'app.id');
  return state.movie[id] || {};
};

export const genreInfo = createSelector(movieInfo, configuration, (movie, conf) => {
  let genres = _.map(movie.genres, (id) => _.get(_.find(conf.genres, { id }), 'title', ''));
  genres = _.filter(genres, (genre) => Boolean(genre));
  return _.get(genres, '0', '');
});

export const seasonsInfo = createSelector(seasonItems, movieInfo, (items, movie) =>
  _.filter(
    items,
    (season) => _.get(movie, 'season_list') && _.includes(movie.season_list, season.id)
  )
);

export const seasonsCountInfo = createSelector(seasonsInfo, (items) => items.length);

export const seriesCountInfo = createSelector(seasonsInfo, (items) => _.sumBy(items, 'total'));

export const restrictionInfo = createSelector(movieInfo, (movie) => _.get(movie, 'restriction'));

export const restrictionsInfo = createSelector(movieInfo, (movie) => _.get(movie, 'restrictions'));

export const restrictionsCodeInfo = createSelector(movieInfo, (movie) =>
  _.get(movie, 'restrictions[0].code')
);

export const imdbRatingInfo = createSelector(movieInfo, (movie) =>
  parseFloat(_.get(movie, 'rating_imdb', 0), 10).toFixed(1)
);

export const megogoRatingInfo = createSelector(movieInfo, (movie) =>
  parseFloat(_.get(movie, 'rating_megogo', 0), 10).toFixed(1)
);

export const movieImageInfo = createSelector(movieInfo, (movie) => _.get(movie, 'image', {}));

export const movieSeriesInfo = createSelector(movieInfo, (movie) => _.get(movie, 'is_series'));

export const movieTrailerIdInfo = createSelector(movieInfo, (movie) => _.get(movie, 'trailer_id'));

export const videoUrlInfo = createSelector(movieInfo, (movie) => _.get(movie, 'video_url'));

export const isAvailableInfo = createSelector(movieInfo, ({ is_available } = {}) => is_available);

export const tariffsCurrencyInfo = createSelector(movieInfo, (movie) => ({
  dto: _.get(movie, 'purchase_info.dto.subscriptions.0.currency', ''),
  tvod: _.get(movie, 'purchase_info.tvod.subscriptions.0.currency', ''),
}));

export const minPriceInfo = createSelector(movieInfo, tariffsCurrencyInfo, (movie, currency) => {
  let result;
  if (movie) {
    const deliveryRules = _.get(movie, 'delivery_rules');
    if (_.isArray(deliveryRules)) {
      const hasDTO = _.includes(deliveryRules, 'dto');
      const hasTVOD = _.includes(deliveryRules, 'tvod');
      const purchaseInfo = _.get(movie, 'purchase_info');
      const minDTO = findMinPrice(purchaseInfo, 'dto');
      const minTVOD = findMinPrice(purchaseInfo, 'tvod');
      if (hasDTO && hasTVOD) {
        if (minDTO !== null && minTVOD !== null) {
          if (minTVOD < minDTO) {
            result = `${minTVOD} ${currency.tvod}`;
          } else {
            result = `${minDTO} ${currency.dto}`;
          }
        } else if (minDTO !== null) {
          result = `${minDTO} ${currency.dto}`;
        } else if (minTVOD !== null) {
          result = `${minTVOD} ${currency.tvod}`;
        }
      } else if (hasDTO) {
        if (minDTO !== null) {
          result = `${minDTO} ${currency.dto}`;
        }
      } else if (hasTVOD) {
        if (minTVOD !== null) {
          result = `${minTVOD} ${currency.tvod}`;
        }
      }
    }
  }
  return result;
});

export const minDTOPriceInfo = createSelector(movieInfo, tariffsCurrencyInfo, (movie, currency) => {
  let result;
  if (movie) {
    const deliveryRules = _.get(movie, 'delivery_rules');
    if (_.isArray(deliveryRules)) {
      const hasDTO = _.includes(deliveryRules, 'dto');
      const purchaseInfo = _.get(movie, 'purchase_info');
      const minDTO = findMinPrice(purchaseInfo, 'dto');
      result = hasDTO ? `${minDTO} ${currency.dto}` : '';
    }
  }
  return result;
});

export const minTVODPriceInfo = createSelector(
  movieInfo,
  tariffsCurrencyInfo,
  (movie, currency) => {
    let result;
    if (movie) {
      const deliveryRules = _.get(movie, 'delivery_rules');
      if (_.isArray(deliveryRules)) {
        const hasTVOD = _.includes(deliveryRules, 'tvod');
        const purchaseInfo = _.get(movie, 'purchase_info');
        const minTVOD = findMinPrice(purchaseInfo, 'tvod');
        result = hasTVOD ? `${minTVOD} ${currency.tvod}` : '';
      }
    }
    return result;
  }
);

export const deliveryInfo = createSelector(movieInfo, (movie) => {
  const deliveryRules = _.get(movie, 'delivery_rules', []);
  return {
    hasADVOD: _.includes(deliveryRules, 'advod'),
    hasTVOD: _.includes(deliveryRules, 'tvod'),
    hasSVOD: _.includes(deliveryRules, 'svod'),
    hasDTO: _.includes(deliveryRules, 'dto'),
    hasFVOD: _.includes(deliveryRules, 'fvod'),
  };
});

export const buyingTariffsInfo = createSelector(movieInfo, (movie) => {
  const result = {};
  if (movie && movie.purchase_info) {
    result.dto = getTariffInfo(movie.purchase_info, 'dto');
    result.tvod = getTariffInfo(movie.purchase_info, 'tvod');
  }
  return result;
});

export const isDTOBoughtInfo = createSelector(movieInfo, (movie) => {
  let result = false;
  const tariffInfo = getTariffInfo(movie.purchase_info, 'dto');
  if (movie && movie.purchase_info) {
    if (tariffInfo.length > 1) {
      for (let i = 0; i < tariffInfo.length; i++) {
        if (_.get(tariffInfo[i], 'isBought', false)) {
          result = true;
        }
      }
    } else {
      result = _.get(getTariffInfo(movie.purchase_info, 'dto'), 'isBought', false);
    }
  }
  return result;
});

export const isTVODBoughtInfo = createSelector(movieInfo, (movie) => {
  let result = false;
  const tariffInfo = getTariffInfo(movie.purchase_info, 'tvod');
  if (movie && movie.purchase_info) {
    if (tariffInfo.length > 1) {
      for (let i = 0; i < tariffInfo.length; i++) {
        if (_.get(tariffInfo[i], 'isBought', false)) {
          result = true;
        }
      }
    } else {
      result = _.get(getTariffInfo(movie.purchase_info, 'tvod'), 'isBought', false);
    }
  }
  return result;
});

export const paidTariff = createSelector(
  idSelector,
  tariffIdPropSelector,
  movieInfo,
  (id, tariffId, movie) => {
    let result = null;
    const purchaseInfo = _.get(movie, 'purchase_info');
    if (purchaseInfo) {
      const tariffs = [...getTariffInfo(purchaseInfo, 'dto')];
      tariffs.push(...getTariffInfo(purchaseInfo, 'tvod'));
      for (let i = 0, l = tariffs.length; i < l; ++i) {
        if (tariffs[i].id === tariffId) {
          result = tariffs[i];
          break;
        }
      }
    }
    return result;
  }
);

export const isEmbedInBizclassSelector = createSelector(
  movieInfo,
  (movie) => (movie.bizclass || []).indexOf('embedded') !== -1
);

export const showGotoMegogoSelector = createSelector(
  isEmbedInBizclassSelector,
  restrictionsCodeInfo,
  restrictionsInfo,
  (isInBizClass, code, restrictions) =>
    !isInBizClass && code === 'BIZ_CLASS' && restrictions.length === 1
);

export default createStructuredSelector({
  movieInfo,
  movieErrorInfo,
  genreInfo,
  seasonsInfo,
  seasonsCountInfo,
  seriesCountInfo,
  restrictionInfo,
  restrictionsInfo,
  restrictionsCodeInfo,
  minPriceInfo,
  tariffsCurrencyInfo,
  deliveryInfo,
  buyingTariffsInfo,
  paidTariff,
  imdbRatingInfo,
  megogoRatingInfo,
  movieImageInfo,
  movieSeriesInfo,
  movieTrailerIdInfo,
  isAvailableInfo,
  minDTOPriceInfo,
  minTVODPriceInfo,
  isDTOBoughtInfo,
  isTVODBoughtInfo,
  videoUrlInfo,
});
